import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Mail, { MailsByDomiciliations } from 'globals/models/Mail'
import MailDetails from 'pages/ClientDetails/components/MailDetails'
import { useLazyQuery, useMutation } from '@apollo/client'
import { MAILS_TO_DESTROY, MailsToDestroyResult, MailsToDestroyVariables } from 'graphql/mails/queries'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import Loader, { LoaderContainer } from 'components/Loader'
import {
  MARK_MAILS_AS_DESTROYED,
  MarkMailsAsDestroyedResult,
  MarkMailsAsDestroyedVariables
} from 'pages/Mails/mutations'
import { displayErrors } from 'globals/graphql'
import { popupGraphQLError } from 'components/GraphQLError'
import popupSuccess from 'globals/popupSuccess'
import SelectList from '../components/SelectList'
import Topbar from '../components/Topbar'
import { ActionBar } from '../components/styles'
import SelectedMailsButtons from 'components/SelectedMailsButtons'
import { SearchBar } from 'components'
import { isMobile } from 'react-device-detect'

const DestroyingMails = () => {
  const pageLength = 5

  const { t } = useTranslation()
  const [mailDetails, setMailDetails] = useState<Mail | null>(null)
  const [showMailDetails, setShowMailDetails] = useState(false)
  const [mailsToDestroy, setMailsToDestroy] = useState<MailsByDomiciliations[]>([])
  const [noMoreMails, setNoMoreMails] = useState(false)
  const [selectedMailsIds, setSelectedMailsIds] = useState<React.Key[]>([])
  const [searchParams, setSearchParams] = useState({ searchTerm: '', currentPage: 1, pageLength: pageLength })

  const [markMailsAsDestroyed] = useMutation<
  MarkMailsAsDestroyedResult, MarkMailsAsDestroyedVariables
  >(MARK_MAILS_AS_DESTROYED, displayErrors)

  const onMailsRetrieved = (mailsToDestroyResult: MailsToDestroyResult) => {
    displayErrors.onCompleted(mailsToDestroyResult)

    if (mailsToDestroyResult) {
      const retrieved = mailsToDestroyResult?.userMailsToDestroy?.userMailsToDestroyByDomiciliation || []
      const currentPage = mailsToDestroyResult?.userMailsToDestroy?.pagination?.currentPage || 0
      const totalPages = mailsToDestroyResult?.userMailsToDestroy?.pagination?.totalPages || 1

      if (currentPage == 1) {
        setMailsToDestroy(retrieved)
      } else {
        setMailsToDestroy((mails) => mails.concat(retrieved))
      }

      if (currentPage >= totalPages) {
        setNoMoreMails(true)
      }

      popupGraphQLError(mailsToDestroyResult)
    }
  }

  const [fetchMailsToDestroy, { loading, data }] = useLazyQuery<MailsToDestroyResult, MailsToDestroyVariables>(
      MAILS_TO_DESTROY,
      {
        ...displayErrors,
        onCompleted: onMailsRetrieved,
        fetchPolicy: 'no-cache'
      }
  )

  const fetchInitialMails = (search: string) => {
    const variables = { search: search, page: 1, pageLength: searchParams.pageLength }
    fetchMailsToDestroy({ variables })
    setSearchParams({ ...searchParams, searchTerm: search, currentPage: 1 })
  }

  const fetchMoreMails = () => {
    fetchMailsToDestroy({
      variables: {
        search: searchParams.searchTerm,
        page: searchParams.currentPage,
        pageLength: searchParams.pageLength
      }
    })
    setSearchParams({ ...searchParams, currentPage: searchParams.currentPage + 1 })
  }

  const [sentryRef] = useInfiniteScroll({
    loading: loading,
    hasNextPage: !noMoreMails &&
      (((data?.userMailsToDestroy?.userMailsToDestroyByDomiciliation || []).length > 0) ||
        mailsToDestroy.length <= 0),
    delayInMs: 1000,
    rootMargin: '0px 0px 400px 0px',
    onLoadMore: fetchMoreMails
  })

  const onConfirm = (ids: React.Key[]) => {
    markMailsAsDestroyed({
      variables: { userMailIds: ids.map(Number) }
    }).then(() => {
      setMailsToDestroy((mailsToDestroy) =>
        mailsToDestroy
          .map((mails) => {
            const userMails = mails.userMails.filter((m) => !ids.includes(m.id))

            return { ...mails, userMails: userMails, userMailsCount: userMails.length }
          })
          .filter((mails) => mails.userMailsCount > 0)
      )
      setSelectedMailsIds([])
      popupSuccess('Courrier(s) marqué(s) comme détruit(s)')
    })
  }

  return (
    <>
      <Topbar
        totalCount={data?.userMailsToDestroy?.pagination?.totalItemsCount || 1}
      />
      <ActionBar>
        <SelectedMailsButtons
          selectedMailsNumber={selectedMailsIds.length}
          confirmText={t('mails.destroyings.confirm')}
          onConfirm={() => onConfirm(selectedMailsIds)}
          onCancel={() => setSelectedMailsIds([])}
        />
        { !isMobile &&
          <SearchBar
            onSearch={() => null}
            onChange={(value: string) => fetchInitialMails(value)}
            value={searchParams.searchTerm}
            hideFilters
            placeholder={'Rechercher ID'}
            marginBottom={false}
            height={'31px'}
            backgroundColor={'#F0F1F3'}
          />
        }
      </ActionBar>
      <SelectList
        userMailsByDomiciliation={mailsToDestroy}
        totalCount={data?.userMailsToDestroy?.pagination?.totalItemsCount || 1}
        lineStyle={'agendaDestroyings'}
        onMailClicked={(mail: Mail) => {
          setMailDetails(mail)
          setShowMailDetails(true)
        }}
        scrollLoader={
          !noMoreMails &&
          <div>
            <LoaderContainer ref={sentryRef}>
              <Loader />
            </LoaderContainer>
          </div>
        }
        defaultChecked
        selectedMailsIds={selectedMailsIds}
        setSelectedMailsIds={setSelectedMailsIds}
      />
      <MailDetails
        mail={mailDetails}
        visible={showMailDetails}
        onClose={() => {
          setShowMailDetails(false)
          setMailDetails(null)
        }}
        disableNpai={true}
      />
    </>
  )
}

export default DestroyingMails
